import React, { useState, useEffect, useMemo } from "react";
import firebase from "../../firebase";
import Service from "../../Service/firebase";
import { isVendorRole, getVendorId } from "../../utils/rbac";
import "./customerlist.css";

const SOURCE_TABS = [
  { key: "all", label: "All" },
  { key: "walkin", label: "Walk-in" },
  { key: "app", label: "App" },
  { key: "both", label: "Both" },
  { key: "converted", label: "Converted" },
];

const formatDate = (ts) => {
  if (!ts) return "—";
  try {
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return "—";
  }
};

const fmtPrice = (val) => {
  const n = parseFloat(val);
  return isNaN(n) ? 0 : n;
};

// Parse a scheduleDate (yyyy-MM-dd) + scheduleTime (HH:mm) into a JS Date for sorting
const apptDate = (appt) => {
  try {
    if (appt.scheduleDate) {
      const [y, m, d] = appt.scheduleDate.split("-").map(Number);
      const [hh = 0, mm = 0] = (appt.scheduleTime || "00:00").split(":").map(Number);
      return new Date(y, m - 1, d, hh, mm);
    }
    if (appt.createdAt?.toDate) return appt.createdAt.toDate();
    return new Date(0);
  } catch {
    return new Date(0);
  }
};

const StatusPill = ({ status }) => {
  const map = {
    Completed: "cust_pill_done",
    Accepted:  "cust_pill_accepted",
    Pending:   "cust_pill_pending",
    Cancelled: "cust_pill_cancelled",
  };
  return <span className={`cust_pill ${map[status] || "cust_pill_pending"}`}>{status || "—"}</span>;
};

const SourceBadge = ({ source, walkinToAppConverted }) => {
  if (walkinToAppConverted) {
    return <span className="cust_badge badge_converted">Converted</span>;
  }
  if (source === "walkin") return <span className="cust_badge badge_walkin">Walk-in</span>;
  if (source === "app") return <span className="cust_badge badge_app">App</span>;
  if (source === "both") return <span className="cust_badge badge_both">Both</span>;
  return <span className="cust_badge badge_walkin">{source || "—"}</span>;
};

// Build per-phone appointment stats from raw appointments array
const buildApptStats = (appointments) => {
  const map = {}; // phone → { visits, spent, lastVisit, appts[] }
  appointments.forEach((appt) => {
    const phone = (appt.phone || "").trim();
    if (!phone) return;
    if (!map[phone]) map[phone] = { visits: 0, spent: 0, lastDate: null, appts: [] };
    if (appt.status === "Completed") {
      map[phone].visits += 1;
      map[phone].spent += fmtPrice(appt.finalPrice || appt.TotalServicePrice);
      const d = apptDate(appt);
      if (!map[phone].lastDate || d > map[phone].lastDate) map[phone].lastDate = d;
    }
    map[phone].appts.push(appt);
  });
  return map;
};

const CustomerList = () => {
  const vendorLocked = isVendorRole();
  const lockedVendorId = vendorLocked ? getVendorId() : null;
  const [allCustomers, setAllCustomers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("all");
  const [searchText, setSearchText] = useState("");
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  // Derived appointment stats keyed by phone
  const apptStats = useMemo(() => buildApptStats(appointments), [appointments]);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const db = firebase.firestore();

        // Fetch appointments (vendor-scoped or all)
        let apptQuery = db.collection("appointments");
        if (lockedVendorId) {
          apptQuery = apptQuery.where("vendorId", "==", lockedVendorId);
        }
        const [custSnap, vendSnap, apptSnap] = await Promise.all([
          Service.getAllData("customers"),
          Service.getAllData("vendors"),
          apptQuery.get(),
        ]);

        const fetchedAppts = apptSnap.docs.map((d) => d.data());
        setAppointments(fetchedAppts);

        // For vendor roles: filter customers by vendors array OR by having appointments
        let filtered;
        if (lockedVendorId) {
          const phonesWithAppts = new Set(fetchedAppts.map((a) => (a.phone || "").trim()).filter(Boolean));
          filtered = custSnap.filter(
            (c) =>
              (Array.isArray(c.vendors) && c.vendors.includes(lockedVendorId)) ||
              phonesWithAppts.has((c.phone || "").trim())
          );
        } else {
          filtered = custSnap;
        }
        setAllCustomers(filtered);
        setCustomers(filtered);
        setVendors(vendSnap);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    let filtered = [...allCustomers];

    if (activeTab === "walkin") {
      filtered = filtered.filter((c) => c.source === "walkin" && !c.walkinToAppConverted);
    } else if (activeTab === "app") {
      filtered = filtered.filter((c) => c.source === "app" && !c.walkinToAppConverted);
    } else if (activeTab === "both") {
      filtered = filtered.filter((c) => c.source === "both");
    } else if (activeTab === "converted") {
      filtered = filtered.filter((c) => c.walkinToAppConverted === true);
    }

    if (searchText.trim()) {
      const q = searchText.toLowerCase();
      filtered = filtered.filter(
        (c) =>
          (c.name || "").toLowerCase().includes(q) ||
          (c.phone || "").includes(q)
      );
    }

    setCustomers(filtered);
  }, [activeTab, searchText, allCustomers]);

  const getVendorName = (uid) => {
    const v = vendors.find((v) => v.uid === uid);
    return v ? v.vendorCode || v.name || uid : uid;
  };

  // Get stats for a customer — prefer appointment-derived data (fixes call booking ₹0 issue)
  const getStats = (c) => {
    const phone = (c.phone || "").trim();
    const fromAppts = apptStats[phone];
    if (fromAppts) {
      return {
        visits: fromAppts.visits,
        spent: fromAppts.spent,
        lastVisit: fromAppts.lastDate,
      };
    }
    // Fallback to Firestore customer doc fields
    return {
      visits: vendorLocked
        ? (c.vendorStats?.[lockedVendorId]?.visits ?? 0)
        : (c.visitCount || 0),
      spent: vendorLocked
        ? (c.vendorStats?.[lockedVendorId]?.spent || 0)
        : (c.totalSpent || 0),
      lastVisit: vendorLocked
        ? c.vendorStats?.[lockedVendorId]?.lastVisit
        : c.lastVisit,
    };
  };

  // Customer's appointment list (sorted newest first)
  const getCustomerAppts = (c) => {
    const phone = (c.phone || "").trim();
    const list = (apptStats[phone]?.appts || []).slice();
    list.sort((a, b) => apptDate(b) - apptDate(a));
    return list;
  };

  const tabCounts = {
    all: allCustomers.length,
    walkin: allCustomers.filter((c) => c.source === "walkin" && !c.walkinToAppConverted).length,
    app: allCustomers.filter((c) => c.source === "app" && !c.walkinToAppConverted).length,
    both: allCustomers.filter((c) => c.source === "both").length,
    converted: allCustomers.filter((c) => c.walkinToAppConverted === true).length,
  };

  // All stats derived from completed appointments only
  const summaryStats = useMemo(() => {
    const completed = appointments.filter((a) => a.status === "Completed");
    const totalVisits    = completed.length;
    const walkInVisits   = completed.filter((a) => a.isWalkIn === true && !a.isCallBooking).length;
    const callVisits     = completed.filter((a) => a.isCallBooking === true).length;
    const totalRevenue   = completed.reduce((s, a) => s + fmtPrice(a.finalPrice || a.TotalServicePrice), 0);
    const avgPerVisit    = totalVisits > 0 ? totalRevenue / totalVisits : 0;

    // Repeat customers = unique phones with ≥ 2 completed visits
    const phoneVisits = {};
    completed.forEach((a) => {
      const p = (a.phone || "").trim();
      if (p) phoneVisits[p] = (phoneVisits[p] || 0) + 1;
    });
    const repeatCustomers = Object.values(phoneVisits).filter((v) => v >= 2).length;

    return { totalVisits, walkInVisits, callVisits, totalRevenue, avgPerVisit, repeatCustomers };
  }, [appointments]);

  if (isLoading) {
    return (
      <div className="cust_page">
        <i className="fas fa-spinner fa-pulse fa-2x"></i>
      </div>
    );
  }

  return (
    <div className="cust_page">
      <div className="cust_header">
        <div>
          <h4 className="cust_title">Customers</h4>
          <p className="cust_subtitle">{allCustomers.length} total customers</p>
        </div>
        <div className="cust_search_wrap">
          <i className="fa fa-search cust_search_icon"></i>
          <input
            type="text"
            className="cust_search"
            placeholder="Search by name or phone..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
        </div>
      </div>

      {/* Tab bar */}
      <div className="cust_tabs">
        {SOURCE_TABS.map((tab) => (
          <button
            key={tab.key}
            className={`cust_tab ${activeTab === tab.key ? "cust_tab_active" : ""}`}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.label}
            <span className="cust_tab_count">{tabCounts[tab.key]}</span>
          </button>
        ))}
      </div>

      {/* Stats row */}
      <div className="cust_stats_row">
        <div className="cust_stat_card">
          <i className="fa fa-users cust_stat_icon"></i>
          <div className="cust_stat_value">{allCustomers.length}</div>
          <div className="cust_stat_label">Total Customers</div>
        </div>
        <div className="cust_stat_card">
          <i className="fa fa-check-circle cust_stat_icon" style={{ color: "#43a047" }}></i>
          <div className="cust_stat_value">{summaryStats.totalVisits}</div>
          <div className="cust_stat_label">Completed Visits</div>
        </div>
        <div className="cust_stat_card">
          <i className="fa fa-male cust_stat_icon" style={{ color: "#1976d2" }}></i>
          <div className="cust_stat_value">{summaryStats.walkInVisits}</div>
          <div className="cust_stat_label">Walk-in Visits</div>
        </div>
        <div className="cust_stat_card">
          <i className="fa fa-phone cust_stat_icon" style={{ color: "#7b1fa2" }}></i>
          <div className="cust_stat_value">{summaryStats.callVisits}</div>
          <div className="cust_stat_label">Call Bookings</div>
        </div>
        <div className="cust_stat_card cust_stat_highlight">
          <i className="fa fa-repeat cust_stat_icon" style={{ color: "#e65100" }}></i>
          <div className="cust_stat_value">{summaryStats.repeatCustomers}</div>
          <div className="cust_stat_label">Repeat Customers</div>
        </div>
        <div className="cust_stat_card">
          <i className="fa fa-rupee cust_stat_icon" style={{ color: "#2e7d32" }}></i>
          <div className="cust_stat_value">₹{summaryStats.totalRevenue.toLocaleString("en-IN")}</div>
          <div className="cust_stat_label">Total Revenue</div>
        </div>
        <div className="cust_stat_card">
          <i className="fa fa-line-chart cust_stat_icon" style={{ color: "#0288d1" }}></i>
          <div className="cust_stat_value">₹{Math.round(summaryStats.avgPerVisit).toLocaleString("en-IN")}</div>
          <div className="cust_stat_label">Avg per Visit</div>
        </div>
      </div>

      {/* Table */}
      {customers.length === 0 ? (
        <div className="cust_empty">No customers found</div>
      ) : (
        <div className="cust_table_wrap">
          <table className="cust_table">
            <thead>
              <tr>
                <th>#</th>
                <th>Name</th>
                <th>Phone</th>
                <th>Source</th>
                <th>Visits</th>
                <th>Total Spent</th>
                <th>Last Visit</th>
                {!vendorLocked && <th>Vendors</th>}
                <th>Details</th>
              </tr>
            </thead>
            <tbody>
              {customers.map((c, idx) => {
                const stats = getStats(c);
                const isOpen = selectedCustomer?.customerId === c.customerId;
                const colSpan = vendorLocked ? 8 : 9;
                return (
                  <React.Fragment key={c.customerId || idx}>
                  <tr className={isOpen ? "cust_row_selected" : ""}>
                    <td>{idx + 1}</td>
                    <td className="cust_name">{c.name || "—"}</td>
                    <td>{c.phone || "—"}</td>
                    <td>
                      <SourceBadge source={c.source} walkinToAppConverted={c.walkinToAppConverted} />
                    </td>
                    <td>{stats.visits}</td>
                    <td className="cust_spent">
                      ₹{stats.spent.toLocaleString("en-IN")}
                    </td>
                    <td>{formatDate(stats.lastVisit)}</td>
                    {!vendorLocked && (
                      <td>
                        <div className="cust_vendor_chips">
                          {(c.vendors || []).slice(0, 3).map((vid) => (
                            <span key={vid} className="cust_vendor_chip">{getVendorName(vid)}</span>
                          ))}
                          {(c.vendors || []).length > 3 && (
                            <span className="cust_vendor_chip cust_vendor_more">
                              +{c.vendors.length - 3}
                            </span>
                          )}
                        </div>
                      </td>
                    )}
                    <td>
                      <button
                        className="cust_detail_btn"
                        onClick={() => setSelectedCustomer(isOpen ? null : c)}
                      >
                        <i className={`fa fa-chevron-${isOpen ? "up" : "down"}`}></i>
                      </button>
                    </td>
                  </tr>

                  {/* Inline expanded row */}
                  {isOpen && (() => {
                    const visitList = getCustomerAppts(c);
                    const completedVisits = visitList.filter((a) => a.status === "Completed");
                    const custWalkIn  = completedVisits.filter((a) => a.isWalkIn && !a.isCallBooking).length;
                    const custCall    = completedVisits.filter((a) => a.isCallBooking).length;
                    const custAvg     = stats.visits > 0 ? Math.round(stats.spent / stats.visits) : 0;
                    return (
                      <tr className="cust_inline_row">
                        <td colSpan={colSpan} className="cust_inline_cell">
                          <div className="cust_detail_panel">
                            <div className="cust_detail_header">
                              <div>
                                <h5>{c.name}</h5>
                                <span>{c.phone}</span>
                                {c.conversionDate && (
                                  <span className="cust_conversion_date">
                                    &nbsp;· Converted on {formatDate(c.conversionDate)}
                                  </span>
                                )}
                              </div>
                              <button className="cust_close_btn" onClick={() => setSelectedCustomer(null)}>
                                <i className="fa fa-times"></i>
                              </button>
                            </div>

                            {/* Summary stats */}
                            <div className="cust_visit_summary">
                              <div className="cust_vs_card">
                                <i className="fa fa-check-circle" style={{ color: "#43a047" }}></i>
                                <div>
                                  <div className="cust_vs_val">{stats.visits}</div>
                                  <div className="cust_vs_lbl">Completed Visits</div>
                                </div>
                              </div>
                              <div className="cust_vs_card">
                                <i className="fa fa-rupee" style={{ color: "#2e7d32" }}></i>
                                <div>
                                  <div className="cust_vs_val">₹{stats.spent.toLocaleString("en-IN")}</div>
                                  <div className="cust_vs_lbl">Total Spent</div>
                                </div>
                              </div>
                              <div className="cust_vs_card">
                                <i className="fa fa-line-chart" style={{ color: "#0288d1" }}></i>
                                <div>
                                  <div className="cust_vs_val">₹{custAvg.toLocaleString("en-IN")}</div>
                                  <div className="cust_vs_lbl">Avg per Visit</div>
                                </div>
                              </div>
                              <div className="cust_vs_card">
                                <i className="fa fa-male" style={{ color: "#1976d2" }}></i>
                                <div>
                                  <div className="cust_vs_val">{custWalkIn}</div>
                                  <div className="cust_vs_lbl">Walk-ins</div>
                                </div>
                              </div>
                              <div className="cust_vs_card">
                                <i className="fa fa-phone" style={{ color: "#7b1fa2" }}></i>
                                <div>
                                  <div className="cust_vs_val">{custCall}</div>
                                  <div className="cust_vs_lbl">Call Bookings</div>
                                </div>
                              </div>
                              <div className="cust_vs_card">
                                <i className="fa fa-clock-o" style={{ color: "#757575" }}></i>
                                <div>
                                  <div className="cust_vs_val">{formatDate(stats.lastVisit)}</div>
                                  <div className="cust_vs_lbl">Last Visit</div>
                                </div>
                              </div>
                            </div>

                            {/* Visit history */}
                            <div className="cust_vh_section">
                              <div className="cust_vh_title">
                                <i className="fa fa-history"></i> Visit History
                              </div>
                              {visitList.length === 0 ? (
                                <div className="cust_vh_empty">No visits recorded</div>
                              ) : (
                                <div className="cust_vh_table_wrap">
                                  <table className="cust_vh_table">
                                    <thead>
                                      <tr>
                                        <th>#</th>
                                        <th>Date</th>
                                        <th>Time</th>
                                        <th>Services</th>
                                        <th>Stylist</th>
                                        <th>Amount</th>
                                        <th>Payment</th>
                                        <th>Status</th>
                                        {!vendorLocked && <th>Salon</th>}
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {visitList.map((appt, i) => {
                                        const price = fmtPrice(appt.finalPrice || appt.TotalServicePrice);
                                        const svcNames = (appt.services || []).map((s) => s.name || s.serviceName || "").filter(Boolean).join(", ");
                                        const stylist = appt.stylistName || appt.stylistId || "—";
                                        return (
                                          <tr key={appt.bookingId || appt.id || i}>
                                            <td>{i + 1}</td>
                                            <td style={{ whiteSpace: "nowrap" }}>
                                              {appt.scheduleDate || formatDate(appt.createdAt)}
                                              {(appt.isCallBooking || appt.isWalkIn) && (
                                                <span className={`cust_appt_tag ${appt.isCallBooking ? "tag_call" : "tag_walkin"}`}>
                                                  {appt.isCallBooking ? "Call" : "Walk-in"}
                                                </span>
                                              )}
                                            </td>
                                            <td>{appt.scheduleTime || "—"}</td>
                                            <td className="cust_vh_svc">{svcNames || "—"}</td>
                                            <td>{stylist}</td>
                                            <td className="cust_vh_price">
                                              {price > 0 ? `₹${price.toLocaleString("en-IN")}` : "—"}
                                            </td>
                                            <td>{appt.paymentMode || "—"}</td>
                                            <td><StatusPill status={appt.status} /></td>
                                            {!vendorLocked && <td style={{ fontSize: 11, color: "#888" }}>{appt.salonName || "—"}</td>}
                                          </tr>
                                        );
                                      })}
                                    </tbody>
                                  </table>
                                </div>
                              )}
                            </div>

                            {/* Org role: vendor breakdown */}
                            {!vendorLocked && Object.keys(c.vendorStats || {}).length > 0 && (
                              <div className="cust_vendor_stats" style={{ marginTop: 16 }}>
                                <h6>Vendor Breakdown (from customer record)</h6>
                                <div className="cust_vendor_stat_grid">
                                  {Object.entries(c.vendorStats || {}).map(([vid, vstats]) => (
                                    <div key={vid} className="cust_vendor_stat_card">
                                      <div className="cust_vendor_stat_name">{getVendorName(vid)}</div>
                                      <div className="cust_vendor_stat_row">
                                        <span>Visits</span>
                                        <strong>{vstats.visits || 0}</strong>
                                      </div>
                                      <div className="cust_vendor_stat_row">
                                        <span>Spent</span>
                                        <strong>₹{(vstats.spent || 0).toLocaleString("en-IN")}</strong>
                                      </div>
                                      <div className="cust_vendor_stat_row">
                                        <span>Last Visit</span>
                                        <strong>{formatDate(vstats.lastVisit)}</strong>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })()}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default CustomerList;
